import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { clientAPI, settingsAPI } from '../services/api';
import Layout from '../components/Layout';
import './Clients.css';

// Line Icons Component (Marshmallow Theme)
const Icon = ({ name, size = 20, color = "currentColor" }) => {
  const icons = {
    building: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="3" ry="3" /><path d="M9 22v-4h6v4" /></svg>,
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="3" ry="3" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    currency: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><path d="M12 18V6" /></svg>,
    plus: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>,
  };
  return icons[name] || null;
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [orgSettings, setOrgSettings] = useState(null);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientsRes, orgRes] = await Promise.all([
        clientAPI.getAll(),
        settingsAPI.getOrganization()
      ]);
      setClients(clientsRes.data.data || []);
      setOrgSettings(orgRes.data.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.contactPerson && client.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'Active' && client.isActive) ||
      (filterStatus === 'Inactive' && !client.isActive);
    return matchesSearch && matchesStatus;
  });

  const currencyCode = orgSettings?.baseCurrency || 'USD';
  const currencyLocale = currencyCode === 'INR' ? 'en-IN' : 'en-US';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.isActive).length,
    totalRevenue: clients.reduce((sum, c) => sum + (c.contractValue || 0), 0),
    activeProjects: clients.reduce((sum, c) => sum + (c.activeProjects || 0), 0)
  };

  const canManageClients = ['Operations Head', 'Project Manager', 'Admin'].includes(user?.role);

  return (
    <Layout>
      <div className="marsh-client-container">
        <header className="marsh-client-header">
          <div className="header-info">
            <h1>Client Management</h1>
            <p>Maintain precise relationships and monitor active contracts</p>
          </div>
          {canManageClients && (
            <button className="marsh-btn-add" onClick={() => navigate('/clients/add')}>
              <Icon name="plus" size={18} />
              <span>Register New Client</span>
            </button>
          )}
        </header>

        {/* Stats Grid - Aligned with the Marshmallow Theme */}
        <div className="marsh-stats-row">
          <div className="marsh-stat-card purple">
            <span className="marsh-stat-label">Total Portfolio</span>
            <span className="marsh-stat-value">{stats.total} Clients</span>
          </div>
          <div className="marsh-stat-card green">
            <span className="marsh-stat-label">Active Projects</span>
            <span className="marsh-stat-value">{stats.activeProjects} Live</span>
          </div>
          <div className="marsh-stat-card amber">
            <span className="marsh-stat-label">Managed Value</span>
            <span className="marsh-stat-value">{formatCurrency(stats.totalRevenue)}</span>
          </div>
        </div>

        {/* Filters Section */}
        <div className="marsh-filters-container">
          <div className="marsh-search-group">
            <Icon name="search" size={18} color="#7c3aed" />
            <input
              type="text"
              placeholder="Search by client name or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="marsh-filter-group">
            <select
              className="marsh-minimal-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="Active">Active Only</option>
              <option value="Inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* List View Table */}
        <div className="marsh-list-container">
          <div className="marsh-list-header">
            <div className="col-client">Client Entity</div>
            <div className="col-contact">Contact Person</div>
            <div className="col-projects">Projects</div>
            <div className="col-value">Value</div>
            <div className="col-status">Status</div>
            <div className="col-actions"></div>
          </div>

          {loading ? (
            <div className="marsh-empty-state">Analyzing records...</div>
          ) : filteredClients.length === 0 ? (
            <div className="marsh-empty-state">
              <p>No client records found matching your criteria.</p>
            </div>
          ) : (
            filteredClients.map(client => (
              <div key={client._id} className="marsh-list-row">
                <div className="col-client">
                  <div className="client-meta-wrapper">
                    <div className="client-initials">
                      {client.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="client-primary-name">{client.name}</div>
                      <div className="client-sub-text">{client.industry || 'Industry N/A'}</div>
                    </div>
                  </div>
                </div>
                <div className="col-contact">
                  <div className="contact-name">{client.contactPerson || 'N/A'}</div>
                  <div className="contact-email">{client.email || client.communicationEmail}</div>
                </div>
                <div className="col-projects">
                  <span className="count-badge">
                    {client.activeProjects || 0} Active
                  </span>
                </div>
                <div className="col-value">
                  <div className="currency-value">{formatCurrency(client.contractValue || 0)}</div>
                </div>
                <div className="col-status">
                  <span className={`marsh-status-pill ${client.isActive ? 'active' : 'inactive'}`}>
                    {client.isActive ? 'Active' : 'Archived'}
                  </span>
                </div>
                <div className="col-actions">
                  <button
                    className="marsh-btn-action-view"
                    onClick={() => navigate(`/clients/${client._id}`)}
                  >
                    View details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Clients;
